import { useState, useEffect, useCallback, useRef } from "react";
import config from "@/config";
import type { DashboardStatsQuery, TransitionRetryStats } from "@/types";
import { buildStatsQueryParams } from "@/lib/dashboard-api";

type PaEnums = { fail_reasons: string[]; pa_statuses: string[] };

type UseTransitionOpts = {
  /** Bumps when the parent “refresh” is clicked so the chart refetches with same query. */
  refetchKey?: number;
};

export function useTransitionRetryStats(
  caseFilters: DashboardStatsQuery,
  fromPathStatus: string[],
  toPathStatus: string[],
  options?: UseTransitionOpts,
) {
  const refetchKey = options?.refetchKey ?? 0;
  const [data, setData] = useState<TransitionRetryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = buildStatsQueryParams(caseFilters);
      for (const f of fromPathStatus) {
        if (f?.trim()) params.append("from_status", f.trim());
      }
      for (const p of toPathStatus) {
        if (p?.trim()) params.append("to_status", p.trim());
      }
      const qs = params.toString();
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/transition-retry-stats${qs ? `?${qs}` : ""}`,
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Request failed: ${res.status}`);
      }
      const json = (await res.json()) as TransitionRetryStats;
      if (seq !== fetchSeqRef.current) return;
      setData(json);
    } catch (e) {
      if (seq !== fetchSeqRef.current) return;
      setError(
        e instanceof Error ? e.message : "Failed to load transition stats",
      );
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [caseFilters, fromPathStatus, toPathStatus]);

  useEffect(() => {
    void fetchData();
  }, [fetchData, refetchKey]);

  return { data, loading, error, refresh: fetchData };
}

export function usePaStructuredEnums() {
  const [enums, setEnums] = useState<PaEnums | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${config.API_BASE_URL}/api/meta/pa-structured-enums`);
        if (!res.ok) throw new Error(`Failed to load enums: ${res.status}`);
        const d = (await res.json()) as PaEnums;
        if (c) setEnums(d);
      } catch (e) {
        if (c) {
          setError(
            e instanceof Error ? e.message : "Failed to load enums",
          );
        }
      } finally {
        if (c) setLoading(false);
      }
    })();
    return () => {
      c = false;
    };
  }, []);

  return { enums, loading, error };
}
